import {
  SessionExpiredError,
  isNotAuthenticatedStatus,
  isSessionExpired,
  markSessionExpired
} from './sessionExpiry';

/**
 * Reads the body without spending it.
 *
 * The caller still owns `response`, so the check has to run against a clone —
 * a 401 that turns out *not* to be an expiry is handed back intact for the
 * caller to parse itself.
 */
const readBody = async (response: Response): Promise<unknown> => {
  try {
    return await response.clone().json();
  } catch {
    return undefined;
  }
};

/**
 * `fetch` for wevr's own authenticated API routes.
 *
 * Drop-in for `fetch` except that an expired Auth0 session becomes a
 * `SessionExpiredError` — which raises the modal — instead of a 401 body the
 * caller would go on to parse as data. Everything else, including other
 * non-2xx statuses, is returned untouched so existing `response.ok` handling
 * keeps working.
 *
 * Do not use it for `/api/config`: that route is unauthenticated, so it has no
 * 401 to interpret.
 */
export async function apiFetch(
  input: RequestInfo,
  init?: RequestInit
): Promise<Response> {
  // Once the session is known dead, every further call is guaranteed to 401.
  // Failing before the network call is what keeps react-query and SWR retries
  // from replaying the storm this fix exists to stop.
  if (isSessionExpired()) {
    throw new SessionExpiredError();
  }

  const response = await fetch(input, init);

  if (response.status === 401) {
    const body = await readBody(response);
    if (isNotAuthenticatedStatus(response.status, body)) {
      markSessionExpired();
      throw new SessionExpiredError();
    }
  }

  return response;
}

export default apiFetch;
